import React from 'react';
import ReactDOM from 'react-dom';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { AboutContent, EntityAboutCard } from '@backstage/plugin-catalog';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { isAdminRole, isTeacherRole, isStudentRole } from '../../utils/roles';
import { ManagedComponentsResolver } from '../../utils/managedComponents';
import { InfoCard } from '@backstage/core-components';
import { DeleteComponentButton } from './DeleteComponentButton';
import { UpdateComponentButton } from './UpdateComponentButton';
import { RestartComponentButton } from './RestartComponentButton';

const useStyles = makeStyles((theme) => ({
  aboutCardContainer: {
    height: '100%',
    position: 'relative',
    // Ensure the card can accommodate our additional button
    '& .MuiCard-root': {
      overflow: 'visible',
      height: '100%',
      position: 'relative',
    },
    // Target the card header to make space for our button
    '& .MuiCardHeader-root': {
      position: 'relative',
    },
    // Ensure the action area is properly positioned
    '& .MuiCardHeader-action': {
      marginTop: theme.spacing(-1),
      marginRight: theme.spacing(-1),
      display: 'flex',
      alignItems: 'center',
      gap: 0,
      '& > *': {
        margin: '0 !important',
        padding: `${theme.spacing(1)}px !important`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        boxSizing: 'border-box',
      },
      '& > * + *': {
        marginLeft: `${theme.spacing(1)}px !important`,
      },
      '& button, & a': {
        padding: '12px !important',
        borderRadius: '50% !important',
        fontSize: '1.5rem !important',
      },
      '& a .MuiTypography-root': {
        display: 'none',
      },
    },
  },
  deleteButtonContainer: {},
  updateButtonContainer: {
    display: 'none',
  },
  hideHeaderActions: {},
  equalHeightCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  ctaContainer: {
    marginTop: theme.spacing(2),
  },
  ctaPortal: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    textAlign: 'right',
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  withContentBottomPadding: {
    '& .MuiCardContent-root': {
      paddingBottom: theme.spacing(7),
    },
  },
  ctaFixedBottom: {
    position: 'absolute',
    left: theme.spacing(2),
    right: theme.spacing(2),
    bottom: theme.spacing(2),
  },
}));

export const CustomEntityAboutCard = (props: any) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  // Whether this entity is one we manage via scaffolder action buttons
  // (Update / Restart / Delete / Delete Resources) is now driven entirely
  // by `scaffolder.managedComponents` in app-config — adding a new
  // module type requires only a config entry, no code change here.
  // `isDjangoLike` controls Django-specific copy ("Update your application
  // here after making changes to your source code…") and is keyed off the
  // resolver's `type` so the same string only appears for the Django
  // module, not for MySQL / future modules.
  const managed = React.useMemo(
    () => new ManagedComponentsResolver(configApi),
    [configApi],
  );
  const detectedManagedType = React.useMemo(
    () => managed.detectType(entity),
    [managed, entity],
  );
  const isManagedComponent = detectedManagedType !== null;
  const isDjangoLike = detectedManagedType?.type === 'django';
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const isComponentKind = ((entity?.kind as string) || '').toLowerCase() === 'component';
  const cardRootRef = React.useRef<HTMLDivElement | null>(null);

  // Check user role and permissions via JWT token (consistent with backend)
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ownershipEntityRefs = identity.ownershipEntityRefs ?? [];
        
        if (process.env.NODE_ENV !== 'production') console.log('CustomEntityAboutCard - ownershipEntityRefs:', ownershipEntityRefs);
        
        // Determine user role (shared util)
        const isUserAdmin = isAdminRole(ownershipEntityRefs, configApi);
        const isUserTeacher = isTeacherRole(ownershipEntityRefs, configApi);
        const isUserStudent = isStudentRole(ownershipEntityRefs, configApi);
        
        if (process.env.NODE_ENV !== 'production') console.log('CustomEntityAboutCard - roles:', { isUserAdmin, isUserTeacher, isUserStudent });
        
        if (mounted) setIsAdmin(isUserAdmin);
      } catch (error) {
        console.error('Error checking admin status:', error);
        if (mounted) setIsAdmin(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, configApi]);

  // Role helpers moved to shared util

  // Only admins can see edit/refresh (About card header actions)
  const canEdit = isAdmin;
  const [cardContentEl, setCardContentEl] = React.useState<Element | null>(null);

  // Admins get Backstage's stock EntityAboutCard, because that is what provides
  // the header's "Schedule entity refresh" -- the only manual resync-to-git
  // trigger in the UI -- and "Edit Metadata". Being a stock component it cannot
  // take our action row as children, so the row is portalled into its
  // CardContent instead. Students get the custom InfoCard below and need none
  // of this.
  //
  // The previous version also hunted for the header action area by matching
  // MuiCardHeader-action plus the refresh/edit button titles, to slot a delete
  // icon in beside them. That button is gone and the header is stock again, so
  // the class-name and title matching -- which would have broken on any
  // Backstage or MUI upgrade -- went with it.
  React.useEffect(() => {
    let observer: MutationObserver | undefined;
    const findCardContent = () => {
      const root = cardRootRef.current;
      if (!root) return false;
      const el = root.querySelector('[class*="MuiCardContent-root"]');
      if (el) {
        setCardContentEl(el);
        return true;
      }
      return false;
    };

    if (findCardContent()) return;

    observer = new MutationObserver(() => {
      if (findCardContent()) observer && observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer && observer.disconnect();
    };
  }, []);
  
  return (
    <div className={classes.aboutCardContainer}>
      {canEdit ? (
        <div className={classes.equalHeightCard + ' ' + classes.withContentBottomPadding}>
        <div ref={cardRootRef}>
          <EntityAboutCard {...props} />
        </div>
        {null}
        </div>
      ) : (
        <div className={classes.equalHeightCard}>
          <InfoCard title="About" variant={props.variant}>
            <AboutContent entity={entity} />
            {isComponentKind && isManagedComponent && (
              <Box className={classes.ctaContainer}>
                {isDjangoLike && (
                  <Typography variant="body2" color="textSecondary">
                    Update your application here after making changes to your source code.
                  </Typography>
                )}
                <Box mt={1} className={classes.buttonContainer}>
                  <UpdateComponentButton render="button" label="Update" />
                  <RestartComponentButton render="button" label="Restart" />
                  <DeleteComponentButton render="button" label="Delete" />
                </Box>
              </Box>
            )}
          </InfoCard>
        </div>
      )}
      {canEdit && isComponentKind && isManagedComponent && cardContentEl && ReactDOM.createPortal(
        <Box className={classes.ctaContainer}>
          {isDjangoLike && (
            <Typography variant="body2" color="textSecondary">
              Update your application here after making changes to your source code.
            </Typography>
          )}
          <Box mt={1} className={classes.buttonContainer}>
            <UpdateComponentButton render="button" label="Update" />
            <RestartComponentButton render="button" label="Restart" />
            <DeleteComponentButton render="button" label="Delete" />
          </Box>
        </Box>,
        cardContentEl
      )}
    </div>
  );
};